import apiClient from "@shared/services/apiClient";

export type ResolveOrgInput = {
  slug?: string;
  email?: string;
  hostname?: string;
};

export type ResolveOrgOrganization = {
  organization_id?: string;
  slug?: string;
  name?: string;
  domain?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  favicon_url?: string | null;
  login_bg_url?: string | null;
  font_family?: string | null;
  show_portal_name?: boolean | null;
  custom_css?: string | null;
  support_email?: string | null;
  tier?: string | null;
  api_url?: string | null;
};

export type ResolveOrgLogin = {
  allowed_methods?: string[];
  effective_allowed_methods?: string[];
  fallback_allowed_methods?: string[];
  sso_providers?: string[];
  sso_providers_config?: Array<{
    config_id?: string;
    provider?: string;
    button_label?: string;
    button_order?: number;
  }>;
  sso_button_label?: string | null;
  sso_unavailable?: boolean;
  require_passkey_for_privileged?: boolean;
  allow_discoverable_passkeys?: boolean;
  require_passkey_user_verification?: boolean;
  login_policy_version?: string;
};

export type ResolveOrgCompliance = {
  profile?: "eu_gdpr" | "us_hipaa" | "global";
  policy_version?: string;
};

export type ResolveOrgCaptcha = {
  preferred_provider?: "recaptcha" | "turnstile";
  available_providers?: Array<"recaptcha" | "turnstile">;
  bypassed?: boolean;
  turnstile_configured?: boolean;
  recaptcha_v2_configured?: boolean;
  recaptcha_v3_configured?: boolean;
  turnstile_site_key?: string | null;
  recaptcha_v2_site_key?: string | null;
  recaptcha_v3_site_key?: string | null;
};

export type ResolveOrgPayload = {
  resolved?: boolean;
  organization?: ResolveOrgOrganization | null;
  login?: ResolveOrgLogin | null;
  compliance?: ResolveOrgCompliance | null;
  captcha?: ResolveOrgCaptcha | null;
};

type ResolveOrgOptions = {
  cacheTtlMs?: number;
};

export class ResolveOrgRateLimitError extends Error {
  retryAfterSeconds: number;

  constructor(retryAfterSeconds: number) {
    super("resolve-org rate limited");
    this.name = "ResolveOrgRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const DEFAULT_CACHE_TTL_MS = 5 * 60_000;
const STORAGE_CACHE_PREFIX = "nervedx.resolveOrg.";

const cache = new Map<
  string,
  { expiresAt: number; payload: ResolveOrgPayload | null }
>();
const inFlight = new Map<string, Promise<ResolveOrgPayload | null>>();
const rateLimitedUntil = new Map<string, number>();

function keyForInput(input: ResolveOrgInput): string {
  const slug =
    typeof input.slug === "string" ? input.slug.trim().toLowerCase() : "";
  const email =
    typeof input.email === "string" ? input.email.trim().toLowerCase() : "";
  const hostname =
    typeof input.hostname === "string"
      ? input.hostname.trim().toLowerCase()
      : "";
  return `slug:${slug}|email:${email}|host:${hostname}`;
}

function readPersistentCache(
  key: string,
): { expiresAt: number; payload: ResolveOrgPayload | null } | null {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(`${STORAGE_CACHE_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      expiresAt?: unknown;
      payload?: ResolveOrgPayload | null;
    };
    if (typeof parsed.expiresAt !== "number") return null;
    return {
      expiresAt: parsed.expiresAt,
      payload: parsed.payload ?? null,
    };
  } catch {
    return null;
  }
}

function writePersistentCache(
  key: string,
  value: { expiresAt: number; payload: ResolveOrgPayload | null },
): void {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    window.sessionStorage.setItem(
      `${STORAGE_CACHE_PREFIX}${key}`,
      JSON.stringify(value),
    );
  } catch {
    // Storage can be unavailable in hardened browsers; the in-memory cache still works.
  }
}

function parseRetryAfterSeconds(error: unknown): number {
  const fallback = 300;
  const err = error as {
    response?: {
      headers?: Record<string, unknown>;
      data?: { retryAfter?: unknown };
    };
  };

  const headerValue = err?.response?.headers?.["retry-after"];
  if (typeof headerValue === "string") {
    const parsed = Number.parseInt(headerValue, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  const bodyValue = err?.response?.data?.retryAfter;
  if (
    typeof bodyValue === "number" &&
    Number.isFinite(bodyValue) &&
    bodyValue > 0
  ) {
    return Math.floor(bodyValue);
  }
  if (typeof bodyValue === "string") {
    const parsed = Number.parseInt(bodyValue, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return fallback;
}

export async function resolveOrgForLogin(
  input: ResolveOrgInput,
  options?: ResolveOrgOptions,
): Promise<ResolveOrgPayload | null> {
  const key = keyForInput(input);
  const now = Date.now();
  const cached = cache.get(key);
  const cacheTtlMs = options?.cacheTtlMs ?? DEFAULT_CACHE_TTL_MS;
  const persistentCached = cached || readPersistentCache(key);

  if (persistentCached) {
    cache.set(key, persistentCached);
    if (persistentCached.expiresAt > now) {
      return persistentCached.payload;
    }
  }

  const limitedUntil = rateLimitedUntil.get(key) || 0;
  if (limitedUntil > now) {
    if (persistentCached) return persistentCached.payload;
    throw new ResolveOrgRateLimitError(Math.ceil((limitedUntil - now) / 1000));
  }

  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = (async () => {
    try {
      const response = await apiClient.post("/auth/resolve-org", input);
      const payload =
        (response?.data?.data as ResolveOrgPayload | undefined) || null;
      const nextCache = { expiresAt: Date.now() + cacheTtlMs, payload };
      cache.set(key, nextCache);
      writePersistentCache(key, nextCache);
      return payload;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 429) {
        const retryAfterSeconds = parseRetryAfterSeconds(error);
        rateLimitedUntil.set(key, Date.now() + retryAfterSeconds * 1000);
        if (persistentCached) return persistentCached.payload;
        throw new ResolveOrgRateLimitError(retryAfterSeconds);
      }
      throw error;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}
